import type {
  OracleToken,
  OracleTokenType,
  OracleTranscription,
} from "./types.ts";
import { ORACLE_TOKEN_TYPES } from "./types.ts";

// Oracle entrega timestamps como "5.040s" — los pasamos a number.
function parseTimeString(raw: unknown, context: string): number {
  if (typeof raw !== "string") {
    throw new Error(`${context}: expected string, got ${typeof raw}`);
  }
  if (!raw.endsWith("s")) {
    throw new Error(`${context}: expected "s" suffix, got "${raw}"`);
  }
  const n = Number(raw.slice(0, -1));
  if (!Number.isFinite(n)) {
    throw new Error(`${context}: "${raw}" is not a finite number`);
  }
  if (n < 0) {
    throw new Error(`${context}: negative time "${raw}"`);
  }
  return n;
}

function parseTokenType(raw: unknown, i: number): OracleTokenType {
  if (typeof raw !== "string") {
    throw new Error(`token[${i}]: type must be string`);
  }
  if (!(ORACLE_TOKEN_TYPES as readonly string[]).includes(raw)) {
    throw new Error(
      `token[${i}]: unknown type "${raw}". Known: ${ORACLE_TOKEN_TYPES.join(", ")}`,
    );
  }
  return raw as OracleTokenType;
}

/**
 * Parsea confidence que viene como string ("0.0010") o number (0.0010)
 * según el modelo. Whisper Large v3 Turbo lo emite como string; otros
 * modelos lo emiten como number. Aceptamos ambos.
 */
function parseConfidence(raw: unknown, i: number): number {
  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) {
      throw new Error(`token[${i}]: confidence is not finite (${raw})`);
    }
    return raw;
  }
  if (typeof raw === "string") {
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      throw new Error(`token[${i}]: confidence "${raw}" is not parseable as number`);
    }
    return n;
  }
  throw new Error(`token[${i}]: confidence must be string or number, got ${typeof raw}`);
}

/**
 * Parsea speakerIndex con fallback a 0 cuando falta.
 *
 * Whisper Large v3 Turbo a veces no emite speakerIndex en tokens de
 * baja confianza (especialmente en los primeros segundos antes de
 * "calibrar" la diarización). En ese caso asumimos speaker 0 — el
 * matcher ya maneja casos ambiguos asignando el dominante por overlap.
 */
function parseSpeakerIndex(raw: unknown): number {
  if (typeof raw === "number" && Number.isInteger(raw) && raw >= 0) return raw;
  return 0;
}

function parseToken(raw: unknown, i: number): OracleToken {
  if (typeof raw !== "object" || raw === null) {
    throw new Error(`token[${i}] is not an object`);
  }
  const t = raw as Record<string, unknown>;
  const startTime = parseTimeString(t.startTime, `token[${i}].startTime`);
  const endTime = parseTimeString(t.endTime, `token[${i}].endTime`);
  if (endTime < startTime) {
    throw new Error(
      `token[${i}]: endTime (${endTime}) < startTime (${startTime})`,
    );
  }
  const token: OracleToken = {
    token: String(t.token ?? ""),
    startTime,
    endTime,
    speakerIndex: parseSpeakerIndex(t.speakerIndex),
    confidence: parseConfidence(t.confidence, i),
    type: parseTokenType(t.type, i),
  };
  if (typeof t.languageCode === "string") {
    token.languageCode = t.languageCode;
  }
  return token;
}

export function parseOracleTranscription(rawJson: string): OracleTranscription {
  const data = JSON.parse(rawJson);

  if (data.status !== "SUCCESS") {
    throw new Error(
      `Oracle status is "${data.status}", expected "SUCCESS". ` +
        "The transcription job did not finish successfully.",
    );
  }

  const transcriptions = data.transcriptions;
  if (!Array.isArray(transcriptions) || transcriptions.length === 0) {
    throw new Error("No transcriptions array found in Oracle response.");
  }

  const first = transcriptions[0];
  if (!Array.isArray(first.tokens)) {
    throw new Error("transcriptions[0].tokens is not an array");
  }

  const tokens = first.tokens.map((t: unknown, i: number) => parseToken(t, i));

  // audioFormatDetails puede venir al nivel root (Speech Job) o en otra
  // posición según el modelo. Probamos ambas.
  const af = data.audioFormatDetails ?? first.audioFormatDetails ?? {};

  return {
    speakerCount: Number(first.speakerCount ?? data.speakerCount ?? 1),
    tokens,
    rawTranscription: String(first.transcription ?? ""),
    confidence: Number(first.confidence ?? 0),
    audioFormat: {
      format: String(af.format ?? "unknown"),
      numberOfChannels: Number(af.numberOfChannels ?? 0),
      sampleRateInHz: Number(af.sampleRateInHz ?? 0),
    },
  };
}
